// Iter 278 — Bulk Data Merge Panel
// --------------------------------------------------------------
// Combines today's (or today + yesterday) bulk-data history
// entries into ONE Excel with a continuous SERIAL NUMBER counter
// across invoices.
//   - USA merge:   two sheets (ArticleDetails + SubPieces),
//                  SubPieces SR REF remapped onto the merged
//                  ArticleDetails serial.
//   - Other merge: single ArticleDetails sheet.
// The "Today" button is one-click (merges every eligible entry).
// "Filter & Merge" opens date / service / invoice filters.
// --------------------------------------------------------------
import { useState } from "react";
import axios from "axios";
import { API, getAuthHeader } from "@/App";
import { Button } from "@/components/ui/button";
import { BulkMergeFilterDialog } from "@/components/BulkMergeFilterDialog";
import { Download, Globe, Filter, Loader2 } from "lucide-react";
import { toast } from "sonner";

const REGION_META = {
  usa: {
    label: "USA Merge",
    description: "ArticleDetails + SubPieces, continuous SR across invoices",
    chip: "USA",
    accent: "from-blue-600 to-indigo-700",
    ring: "ring-blue-200",
  },
  other: {
    label: "Other Countries Merge",
    description: "ArticleDetails only, continuous SR across invoices",
    chip: "Other",
    accent: "from-fuchsia-600 to-pink-600",
    ring: "ring-fuchsia-200",
  },
};

const triggerBlobDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  // Defer cleanup — same-tick revoke can cancel the download in Chromium.
  setTimeout(() => { a.remove(); URL.revokeObjectURL(url); }, 1000);
};

const parseFilenameFromCD = (cd, fallback) => {
  if (!cd) return fallback;
  const m = cd.match(/filename="?([^";]+)"?/i);
  return m ? m[1] : fallback;
};

const BulkDataMergePanel = ({ user }) => {
  const [busyToday, setBusyToday] = useState(null);  // 'usa' | 'other' | null
  const [filterDlg, setFilterDlg] = useState({ open: false, region: "usa" });

  const runMerge = async ({ region, scope, ids }) => {
    try {
      const r = await axios.post(
        `${API}/bulk-data/merge`,
        { region, scope, ids: ids || [] },
        { headers: { ...getAuthHeader() }, responseType: "blob" }
      );
      const fn = parseFilenameFromCD(
        r.headers["content-disposition"],
        `${region.toUpperCase()}-MERGED-BULKDATA.xlsx`
      );
      triggerBlobDownload(r.data, fn);
      const skipped = Number(r.headers["x-merge-skipped"] || 0);
      toast.success(`Merged file downloaded — ${fn}`);
      if (skipped) toast.warning(`${skipped} file(s) could not be read — listed in the Merge Summary sheet`);
      return true;
    } catch (e) {
      let msg = "Merge failed";
      // response is a blob when error — try to read JSON
      if (e.response?.data instanceof Blob) {
        try {
          const text = await e.response.data.text();
          msg = JSON.parse(text).detail || msg;
        } catch { /* keep default */ }
      } else {
        msg = e.response?.data?.detail || e.message || msg;
      }
      toast.error(msg);
      return false;
    }
  };

  const handleToday = async (region) => {
    setBusyToday(region);
    await runMerge({ region, scope: "today" });
    setBusyToday(null);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {Object.entries(REGION_META).map(([region, meta]) => (
        <div
          key={region}
          data-testid={`merge-card-${region}`}
          className={`relative rounded-xl border bg-white p-4 shadow-sm ring-1 ${meta.ring}`}
        >
          <div className="flex items-start justify-between mb-3">
            <div>
              <div className="flex items-center gap-2">
                <Globe className="w-4 h-4 text-slate-500" />
                <span className="text-sm font-semibold text-slate-800">{meta.label}</span>
                <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-gradient-to-r ${meta.accent} text-white`}>
                  {meta.chip}
                </span>
              </div>
              <p className="text-[11px] text-slate-500 mt-1">{meta.description}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              onClick={() => handleToday(region)}
              disabled={busyToday === region}
              data-testid={`merge-today-${region}`}
              className={`bg-gradient-to-r ${meta.accent} text-white hover:opacity-95`}
            >
              {busyToday === region ? (
                <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Merging…</>
              ) : (
                <><Download className="w-3.5 h-3.5 mr-1.5" /> Merge Today</>
              )}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setFilterDlg({ open: true, region })}
              data-testid={`merge-select-${region}`}
            >
              <Filter className="w-3.5 h-3.5 mr-1.5" />
              Filter &amp; Merge
            </Button>
          </div>
        </div>
      ))}

      <BulkMergeFilterDialog open={filterDlg.open} initialRegion={filterDlg.region} user={user}
                             onOpenChange={(v) => setFilterDlg(f => ({ ...f, open: v }))} runMerge={runMerge} />
    </div>
  );
};

export default BulkDataMergePanel;
